import React, { useEffect, useRef, useState } from "react";
import Input from "../../../components/Input/Input";
import axios from "axios";
import { backendUrl } from "../../../api/constants";
import { FieldMessage } from "../../../components/Input/InputError";
import ProfilePicture from "../../../components/icons/ProfilePicture";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IoIosArrowBack } from "react-icons/io";
import AppStatusScreen from "../../../components/AppStatusScreen/AppStatusScreen";
import KitButton from "../../../components/kit/Button";
import IconButton from "../../../components/kit/IconButton";
import { Card } from "../../../components/kit/Card";

const ResetPassword = () => {
  const [email, setEmail] = useState();
  const [message, setMessage] = useState();
  const [loading, setLoading] = useState(false);
  const [tokenMessage, setTokenMessage] = useState();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const passwordRef = useRef();
  const confirmPasswordRef = useRef();

  //check if url query contains token
  const processToken = async (token) => {
    //redirect to reset password page
    try {
      const { data } = await axios.get(
        `${backendUrl}/auth/reset-password-token/${token}`
      );
      if (data.error) {
        setTokenMessage({ error: data.error });
      } else {
        setTokenMessage({ success: "token is valid", user: data.user });
      }
    } catch (error) {
      setTokenMessage({
        error: "something went wrong, please try again later",
      });
    }
  };

  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      setMessage();
      processToken(token);
    }
  }, [searchParams]);

  const resetPassword = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${backendUrl}/auth/reset-password-request`,
        { email }
      );
      if (data.error) {
        setMessage({ error: data.error });
      } else {
        setMessage({ success: "check your email" });
      }
    } catch (error) {
      console.log(error);
      setMessage({ error: "something went wrong, please try again later" });
    } finally {
      setLoading(false);
    }
  };

  const reset = async () => {
    if (passwordRef.current.value !== confirmPasswordRef.current.value) {
      setMessage({ error: "passwords do not match" });
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(`${backendUrl}/auth/reset-password`, {
        token: searchParams.get("token"),
        password: passwordRef.current.value,
      });
      if (data.error) {
        setMessage({ error: data.validation?.password?.error || data.error });
      } else {
        setMessage({ success: "password has been reset", reset: true });
      }
    } catch (error) {
      console.log(error);
      setMessage({ error: "something went wrong, please try again later" });
    } finally {
      setLoading(false);
    }
  };

  if (!tokenMessage) {
    return (
      <AppStatusScreen
        title="password reset"
        subtitle="enter your email for a reset link"
        actions={
          <div className="flex flex-col items-center gap-kit-16">
            <KitButton variant="primary" loading={loading} className="w-[14.75rem] max-w-full" onClick={resetPassword}>
              send link
            </KitButton>
            <IconButton size="md" label="back to sign in" icon={<IoIosArrowBack />} onClick={() => navigate("/auth/signin")} />
          </div>
        }
      >
        <div className="inyc-fields mt-kit-32 w-[14.75rem] max-w-full">
          <Input
            placeHolder="email"
            value={email}
            type="email"
            autoComplete="email"
            name="email"
            className="!mt-0"
            inputStyle={{ textAlign: "center" }}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {message && <FieldMessage className="mt-kit-8" message={message} />}
      </AppStatusScreen>
    );
  }

  if (tokenMessage.error) {
    return (
      <AppStatusScreen
        title="reset password"
        subtitle={<FieldMessage>{tokenMessage.error || "something went wrong, please try again later"}</FieldMessage>}
        actions={
          <KitButton
            variant="primary"
            className="w-[14.75rem] max-w-full"
            onClick={() => {
              setTokenMessage();
              const nextParams = new URLSearchParams(searchParams);
              nextParams.delete("token");
              setSearchParams(nextParams);
            }}
          >
            try again
          </KitButton>
        }
      />
    );
  }

  return (
    <AppStatusScreen
      title="reset password"
      actions={
        message?.reset ? (
          <KitButton variant="primary" className="w-[15.75rem] max-w-full" onClick={() => navigate("/auth/signin")}>
            go to sign in
          </KitButton>
        ) : (
          <KitButton variant="primary" loading={loading} className="w-[15.75rem] max-w-full" onClick={reset}>
            reset password
          </KitButton>
        )
      }
    >
      <Card padding="sm" className="mt-kit-24 flex w-fit items-center gap-kit-16 text-start">
        <ProfilePicture
          style={{ width: "3rem", height: "3rem" }}
          profilePicture={tokenMessage.user.profilePicture}
          username={tokenMessage.user.firstName.charAt(0) + tokenMessage.user.lastName.charAt(0)}
        />
        <div>
          <div className="font-['Primary-Medium']">
            {(tokenMessage.user.firstName + " " + tokenMessage.user.lastName).toLowerCase()}
          </div>
          <div className="inyc-help is-sm">{tokenMessage.user.email}</div>
        </div>
      </Card>
      {!message?.reset && (
        <div className="inyc-fields mt-kit-24 flex w-[15.75rem] max-w-full flex-col gap-kit-12">
          <Input Ref={passwordRef} placeHolder="new password" password className="!mt-0" />
          <Input Ref={confirmPasswordRef} placeHolder="confirm password" password className="!mt-0" />
        </div>
      )}
      {message && <FieldMessage className="mt-kit-8" message={message} />}
    </AppStatusScreen>
  );
};

export default ResetPassword;
